<template>
  <v-dialog v-model="dialog" max-width="600" @click:outside="cancel">
    <template v-slot:activator="{ on, attrs }">
      <v-btn block v-bind="attrs" v-on="on" v-if="!hideButton">
        <v-icon>group_add</v-icon> Add Members
      </v-btn>
    </template>
    <v-card flat>
      <template v-if="show_import">
        <v-card-title>
          Import Member Spreadsheet
          <v-spacer></v-spacer>
          <v-btn icon @click="cancel">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          Import members using our
          <a @click="$emit('export')" :class="`${color}--text`"
            >import template</a
          >
        </v-card-subtitle>
        <v-card-text>
          <v-file-input
            v-model="importFile"
            accept=".csv"
            label="Member Import"
            outlined
            :color="color"
          ></v-file-input>
          <v-row dense>
            <v-col cols="auto">
              <v-btn
                outlined
                :color="color"
                :loading="saving"
                @click="$emit('export')"
              >
                <v-icon class="mr-2">download</v-icon>
                Download
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn :color="color" :loading="saving" @click="importUsers">
                <v-icon class="mr-2">cloud_upload</v-icon>
                Upload
              </v-btn>
            </v-col>
            <v-col cols="auto" class="my-auto">
              <a
                href="#"
                :data-beacon-article-modal="$helpscout.IMPORT_CSV"
                class="info--text"
              >
                View import guide
              </a>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-text
          class="pt-4 secondary--text"
          v-bind:style="{
            backgroundColor: getColor($vuetify.theme.themes.light[color], 0.2)
          }"
        >
          <v-icon :color="color" class="mr-2">info</v-icon>
          {{ emailText }}
        </v-card-text>
      </template>
      <template v-else-if="show_blob">
        <v-card-title>
          Add members by email
          <v-spacer></v-spacer>
          <v-btn icon @click="cancel">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          Quickly add your team members using their email address
        </v-card-subtitle>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-textarea
              v-model="emailBlob"
              outlined
              label="Separated your emails by commas or line breaks"
              :error-messages="errorMessages"
              :color="color"
            ></v-textarea>
          </v-form>
          <v-btn :loading="saving" @click="addUsers" :color="color">
            Add Members
          </v-btn>
          <span :class="color + '--text ml-4'">
            {{ emailCount }} emails identified
          </span>
        </v-card-text>
        <v-card-text
          class="pt-4 secondary--text"
          v-bind:style="{
            backgroundColor: getColor($vuetify.theme.themes.light[color], 0.2)
          }"
        >
          <v-icon :color="color" class="mr-2">info</v-icon>
          {{ emailText }}
        </v-card-text>
      </template>
      <template v-else-if="show_form">
        <member-detail-form
          v-on:save="formSave"
          close
          v-on:close="cancel"
        ></member-detail-form>
      </template>
      <template v-else-if="show_invite">
        <v-card-title>
          Build your invite link
          <v-spacer></v-spacer>
          <v-btn icon @click="cancel">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          We'll generate a sign up page for you to share with your team
        </v-card-subtitle>
        <v-card-text>
          <div class="border--bordercolor rounded-lg pa-4">
            <v-row dense>
              <v-col cols="12" class="font-weight-medium subtitle-1">
                Your team members will be asked to provide the follow info
              </v-col>
              <v-col cols="6" sm="4" v-for="(item, i) in inviteFields" :key="i">
                <v-icon x-small color="secondarytext">
                  {{ item.required ? "lock" : "lock_open" }}
                </v-icon>
                {{ item.name }}
              </v-col>
            </v-row>
          </div>
        </v-card-text>
        <v-card-subtitle>
          <v-row dense>
            <v-col class="my-auto">
              Add email validation
            </v-col>
            <v-spacer> </v-spacer>
            <v-col cols="auto">
              <help-tooltip>
                If you’d like to ensure only approved email addresses are used
                by your people during account creation, you can add one or more
                email domains here. Include the @ symbol. (Example @nike.com or
                @nikeinc.com).
              </help-tooltip>
            </v-col>
          </v-row>
        </v-card-subtitle>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-text-field
              v-model="emailDomain"
              outlined
              maxlength="250"
              :rules="[$rules.emailDomain]"
              required
              placeholder="Add required email format (e.g. @company.com)"
              dense
              color="accent"
              validate-on-blur
              clearable
            >
              <template v-slot:append-outer>
                <v-btn
                  height="40"
                  color="accent"
                  @click="addDomain()"
                  :disabled="!emailDomain"
                >
                  Add
                </v-btn>
              </template>
            </v-text-field>
          </v-form>
          <v-row dense>
            <v-col v-for="(domain, i) in domains" :key="i" cols="auto">
              <v-chip color="accent" close @click:close="removeDomain(domain)">
                {{ domain }}
              </v-chip>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn :loading="saving" :color="color" @click="generateLink">
            {{ settings.registrationLink ? "Update Link" : "Generate Link" }}
          </v-btn>
          <v-btn
            text
            v-if="settings.registrationLink"
            color="secondary"
            @click="deleteLink"
          >
            Delete Link
          </v-btn>
        </v-card-actions>
      </template>
      <template v-else>
        <v-card-title>
          We have options for you
          <v-spacer></v-spacer>
          <v-btn icon @click="cancel">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-subtitle>
          How would you like to add your members?
        </v-card-subtitle>
        <v-card-text>
          <v-card outlined @click="show_import = true" class="mb-4">
            <v-container class="py-0">
              <v-row>
                <v-col cols="auto" class="my-auto">
                  <v-icon size="50" :color="color">note_add</v-icon>
                </v-col>
                <v-col cols="auto">
                  <v-card-title>
                    Upload Member Spreadsheet
                  </v-card-title>
                  <v-card-subtitle>
                    Great if you need to add many members
                  </v-card-subtitle>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" class="my-auto">
                  <v-icon size="40" color="secondarytext">
                    keyboard_arrow_right
                  </v-icon>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <v-card outlined @click="show_blob = true" class="mb-4">
            <v-container class="py-0">
              <v-row>
                <v-col cols="auto" class="my-auto">
                  <v-icon size="50" :color="color">group_add</v-icon>
                </v-col>
                <v-col cols="auto">
                  <v-card-title>
                    Add Email Addresses
                  </v-card-title>
                  <v-card-subtitle>
                    Great if you need to add many members
                  </v-card-subtitle>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" class="my-auto">
                  <v-icon size="40" color="secondarytext">
                    keyboard_arrow_right
                  </v-icon>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <v-card outlined @click="show_form = true" class="mb-4">
            <v-container class="py-0">
              <v-row>
                <v-col cols="auto" class="my-auto">
                  <v-icon size="50" :color="color">person_add</v-icon>
                </v-col>
                <v-col cols="auto">
                  <v-card-title>
                    Add One Member
                  </v-card-title>
                  <v-card-subtitle>
                    Skip the spreadsheet and add one at a time
                  </v-card-subtitle>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" class="my-auto">
                  <v-icon size="40" color="secondarytext">
                    keyboard_arrow_right
                  </v-icon>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <v-card outlined @click="show_invite = true">
            <v-container class="py-0">
              <v-row>
                <v-col cols="auto" class="my-auto">
                  <v-icon size="50" :color="color">link</v-icon>
                </v-col>
                <v-col cols="auto">
                  <v-card-title>
                    Share Invite Link
                  </v-card-title>
                  <v-card-subtitle>
                    Share a link with your team to register
                  </v-card-subtitle>
                </v-col>
                <v-spacer></v-spacer>
                <v-col cols="auto" class="my-auto">
                  <v-icon size="40" color="secondarytext">
                    keyboard_arrow_right
                  </v-icon>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
        </v-card-text>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import MemberDetailForm from "@/components/members/MemberDetailForm";
import { mapGetters } from "vuex";
import HelpTooltip from "@/components/common/HelpTooltip";
export default {
  name: "AddMembersDialog",
  components: { MemberDetailForm, HelpTooltip },
  props: {
    hideButton: { type: Boolean, default: false },
    color: { type: String, default: "primary" },
    emailText: {
      type: String,
      default:
        "Members won't be emailed until after your first survey is delivered. At that time they will be able to create their account"
    }
  },
  data: () => ({
    dialog: false,
    valid: false,
    saving: false,
    importFile: null,
    show_import: false,
    show_form: false,
    show_blob: false,
    show_invite: false,
    emailBlob: "",
    errorMessages: [],
    emailCount: 0,
    inviteFields: [
      { required: true, name: "First Name" },
      { required: true, name: "Last Name" },
      { required: true, name: "Email" },
      { required: true, name: "Password" },
      { required: false, name: "Job Title" },
      { required: false, name: "Phone #" }
    ],
    emailDomain: "",
    domains: []
  }),
  watch: {
    dialog(val, oldVal) {
      if (val != oldVal && !val) {
        this.show_import = false;
        this.show_form = false;
        this.show_blob = false;
        this.show_invite = false;

        this.$store.dispatch("user/clearUser");
      }
    },
    emailBlob(val, oldVal) {
      if (val != oldVal) {
        this.emailCount = this.getValidEmails(val).length;
      }
    }
  },
  computed: { ...mapGetters({ settings: "profile/settings" }) },
  created() {
    this.$store.dispatch("user/clearUser");
  },
  methods: {
    cancel() {
      this.dialog = false;
    },
    addDomain() {
      if (this.$refs.form.validate()) {
        this.domains.push(this.emailDomain);
        this.emailDomain = "";
      }
    },
    removeDomain(domain) {
      let index = this.domains.findIndex(x => x == domain);
      if (index >= 0) {
        this.domains.splice(index, 1);
      }
    },
    formSave() {
      this.$emit("save");
      this.dialog = false;
    },
    importUsers() {
      if (this.importFile) {
        this.saving = true;
        this.$store
          .dispatch("user/importUsers", this.importFile)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Members imported");
            this.dialog = false;
            this.$store.dispatch("billing/getBillableUserCount");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while uploading a user import."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    generateLink() {
      this.saving = true;
      this.$store
        .dispatch("user/saveRegistration", this.domains)
        .then(() => {
          this.$emit("save");
          this.$root.$snackbarSuccess("Link generated");
          this.dialog = false;
          this.$store.dispatch("profile/getSettings");
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occurred while generating a link."
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    async deleteLink() {
      if (
        await this.$root.$confirm(
          "Delete link",
          "Are you sure you want to delete this invite link?",
          { color: "red" }
        )
      ) {
        this.saving = true;
        this.$store
          .dispatch("user/deleteRegistration", this.domains)
          .then(() => {
            this.$emit("save");
            this.$root.$snackbarSuccess("Link deleted");
            this.dialog = false;
            this.$store.dispatch("profile/getSettings");
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while deleting a link."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    addUsers() {
      if (this.emailBlob) {
        this.errorMessages = this._.compact([
          this.emailBlob.trim().length == 0 ? "Required" : "",
          this.validateEmailBlob(this.emailBlob)
        ]);
        if (this.errorMessages.length == 0) {
          this.saving = true;
          this.$store
            .dispatch("user/bulkAddUsers", this.parseEmailBlob(this.emailBlob))
            .then(data => {
              this.$emit("save");
              this.$refs.form.reset();
              if (data.emailsAdded.length && !data.emailsNotAdded.length) {
                this.$root.$snackbarSuccess(
                  `${data.emailsAdded.length} member(s) added`
                );
              } else if (data.emailsAdded.length) {
                this.$root.$snackbar(
                  `${data.emailsAdded.length} member(s) added and ${data.emailsNotAdded.length} member(s) already exist`,
                  "warning",
                  "warning"
                );
              } else {
                this.$root.$snackbarError(
                  `${data.emailsNotAdded.length} member(s) already exist`
                );
              }
              this.dialog = false;
              this.$store.dispatch("billing/getBillableUserCount");
            })
            .catch(error => {
              this.logError(error);
              this.$root.$alert(
                "Error Occurred",
                "An error occurred while adding your members."
              );
            })
            .finally(() => {
              this.saving = false;
            });
        }
      } else if (this.importFile) {
        this.saving = true;
        this.$store
          .dispatch("user/importUsers", this.importFile)
          .then(() => {
            this.$emit("save");
            this.$refs.form.reset();
            this.$root.$snackbarSuccess("Members imported");
            this.dialog = false;
          })
          .catch(error => {
            this.logError(error);
            this.$root.$alert(
              "Error Occurred",
              "An error occurred while uploading a user import."
            );
          })
          .finally(() => {
            this.saving = false;
          });
      }
    },
    getValidEmails(v) {
      let self = this;
      let validEmails = [];
      this._.forEach(this.parseEmailBlob(v), function(item) {
        let response = self.$rules.email(item);
        if (response == true) {
          validEmails.push(item);
        }
      });
      return validEmails;
    },
    validateEmailBlob(v) {
      let self = this;
      let invalidEmails = [];
      this._.forEach(this.parseEmailBlob(v), function(item) {
        let response = self.$rules.email(item);
        if (response != true) {
          invalidEmails.push(item);
        }
      });
      if (invalidEmails.length) {
        return "Invalid emails: " + invalidEmails.join(", ");
      }
      return "";
    },
    parseEmailBlob(text) {
      return this._.compact(
        (text || "")
          .split("\n")
          .join(",")
          .split(",")
          .map(x => x.trim())
      );
    },
    showDialog(show_import, show_blob, show_invite) {
      this.dialog = true;
      this.show_import = show_import;
      this.show_blob = show_blob;
      this.show_invite = show_invite;
      this.domains = this.settings.domains || [];
    }
  }
};
</script>

<style lang="scss" scoped>
.v-text-field--enclosed.v-input--dense:not(.v-text-field--solo).v-text-field--outlined ::v-deep .v-input__append-outer {
  margin-top: 0 !important;
}
</style>
